import { createDate } from '@unistocks/shared/utils/util';
import { DateTime, WeekdayNumbers } from 'luxon';
import type { DateStringValue } from '../../types';
import { getEndOfWeek } from '../get-end-of-week/get-end-of-week';
import { getStartOfWeek } from '../get-start-of-week/get-start-of-week';

interface GetMonthDaysInput {
  /** Any date within the month to enumerate, `yyyy-MM-dd`. */
  month: DateStringValue;
  /** First weekday of each row. @default 1 — Monday */
  firstDayOfWeek?: WeekdayNumbers;
  /** Pad to a fixed 6 weeks so the grid height never shifts. */
  consistentWeeks?: boolean;
}

/**
 * Builds the calendar grid for `month` as an array of weeks (each 7
 * `yyyy-MM-dd` strings), including the trailing/leading days needed to fill
 * whole weeks. With `consistentWeeks`, pads to 6 weeks. Mirrors @mantine/dates
 * `getMonthDays`.
 */
export function getMonthDays({
  month,
  firstDayOfWeek = 1,
  consistentWeeks
}: GetMonthDaysInput): DateStringValue[][] {
  const day = createDate(month);
  const startOfMonth = day.startOf('month');
  const endOfMonth = day.endOf('month');
  const endDate = getEndOfWeek(endOfMonth.toISODate()!, firstDayOfWeek);
  const weeks: DateStringValue[][] = [];

  let date = createDate(getStartOfWeek(startOfMonth.toISODate()!, firstDayOfWeek));

  while (date.startOf('day') < DateTime.fromISO(endDate).startOf('day')) {
    const days: DateStringValue[] = [];

    for (let i = 0; i < 7; i += 1) {
      days.push(date.toISODate()!);
      date = date.plus({ days: 1 });
    }

    weeks.push(days);
  }

  if (consistentWeeks && weeks.length < 6) {
    const lastWeek = weeks[weeks.length - 1];
    const lastDay = lastWeek[lastWeek.length - 1];
    let nextDay = createDate(lastDay).plus({ days: 1 });

    while (weeks.length < 6) {
      const days: DateStringValue[] = [];

      for (let i = 0; i < 7; i += 1) {
        days.push(nextDay.toISODate()!);
        nextDay = nextDay.plus({ days: 1 });
      }

      weeks.push(days);
    }
  }

  return weeks;
}
